import * as Sentry from '@sentry/nextjs'
import { Component, ErrorInfo, ReactNode, type ComponentType } from 'react'

type FallbackRender = (args: { error: Error; resetErrorBoundary: () => void }) => ReactNode

interface SentryErrorBoundaryPropsBase {
  children: ReactNode
  /** Identifies which section of the page this boundary wraps (e.g. "search-form", "flight-results", "filters") */
  boundary: string
  /** Page where this boundary lives (e.g. "home", "srp", "itinerary") */
  page?: string
  /**
   * When true, tags the event as P0 in Sentry (`error.priority`). Use for boundaries that wrap a full page or primary page shell.
   * Section-level boundaries omit this (default P1).
   */
  isPageLevel?: boolean
}

type SentryErrorBoundaryProps =
  | (SentryErrorBoundaryPropsBase & { fallback: ReactNode; fallbackRender?: undefined })
  | (SentryErrorBoundaryPropsBase & { fallback?: undefined; fallbackRender: FallbackRender })

interface SentryErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * Error boundary that catches React render errors and reports them to Sentry
 * with structured tags for filtering and grouping.
 *
 * Usage:
 *   <SentryErrorBoundary boundary="search-form" page="home" fallback={<ErrorFallback />}>
 *     <SearchForm />
 *   </SentryErrorBoundary>
 *
 *   <SentryErrorBoundary
 *     boundary="flight-results"
 *     page="srp"
 *     fallbackRender={({ error, resetErrorBoundary }) => (
 *       <ErrorFallback error={error} onRetry={resetErrorBoundary} />
 *     )}
 *   >
 *     <Results />
 *   </SentryErrorBoundary>
 *
 * Sentry tags set on each captured error:
 *   - error.source: "error_boundary"     → filter all boundary errors in dashboards
 *   - error.boundary: value of boundary prop  → filter by section (e.g. "search-form")
 *   - error.page: value of page prop          → filter by page (e.g. "home")
 *   - error.priority: "P0" if isPageLevel, else "P1" (alerting / triage)
 *
 * Fingerprint: groups errors by [boundary, error name, first line of message]
 * so one bug = one Sentry issue, no matter how many users hit it.
 */
class SentryErrorBoundary extends Component<SentryErrorBoundaryProps, SentryErrorBoundaryState> {
  public override state: SentryErrorBoundaryState = {
    hasError: false,
    error: null,
  }

  public static getDerivedStateFromError(error: Error): SentryErrorBoundaryState {
    return { hasError: true, error }
  }

  private resetErrorBoundary = (reloadPage: boolean = false) => {
    if (reloadPage) {
      window.location.reload()
    } else {
      this.setState({ hasError: false, error: null })
    }
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.withScope(scope => {
      // Tags for filtering in Sentry dashboards and alert rules
      scope.setTag('error.source', 'error_boundary')
      scope.setTag('error.boundary', this.props.boundary)

      if (this.props.page) {
        scope.setTag('error.page', this.props.page)
      }

      scope.setTag('error.priority', this.props.isPageLevel ? 'P0' : 'P1')

      // User-visible crashes are always severity "error"
      scope.setLevel('error')

      // Attach React component stack for debugging
      scope.setExtra('componentStack', errorInfo.componentStack)
      scope.setExtra('boundary', this.props.boundary)
      scope.setExtra('page', this.props.page || 'unknown')
      scope.setExtra('isPageLevel', Boolean(this.props.isPageLevel))

      // Fingerprint controls how Sentry groups events into issues.
      // Without this, slightly different stack traces create duplicate issues.
      // With this: 10,000 users hitting the same bug = 1 isseue.
      const firstLine = (error.message || '').split('\n')[0].substring(0, 200)
      scope.setFingerprint(['error_boundary', this.props.boundary, error.name || 'Error', firstLine])

      Sentry.captureException(error)
    })
  }

  public override render() {
    if (this.state.hasError) {
      const error = this.state.error || new Error('Unknown error')
      if (this.props.fallbackRender) {
        return this.props.fallbackRender({ error, resetErrorBoundary: this.resetErrorBoundary })
      }
      return this.props.fallback || <></>
    }

    return this.props.children
  }
}

type WithErrorBoundaryOptions = Omit<SentryErrorBoundaryProps, 'children'>

/**
 * Convenience HOC for wrapping a feature component at export time.
 *
 * Usage:
 *   const Results = (props: Props) => { ... }
 *   export default withErrorBoundary(Results, {
 *     boundary: 'srp-results',
 *     page: 'srp',
 *     fallbackRender: ({ resetErrorBoundary }) => <ErrorFallback onRetry={resetErrorBoundary} />
 *   })
 *
 *   // Pass isPageLevel: true on top-level page shells so Sentry gets error.priority P0 (default is P1).
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: ComponentType<P>,
  options: WithErrorBoundaryOptions
): ComponentType<P> {
  const ComponentWithBoundary = (props: P) =>
    options.fallbackRender ? (
      <SentryErrorBoundary
        boundary={options.boundary}
        page={options.page}
        isPageLevel={options.isPageLevel}
        fallbackRender={options.fallbackRender}
      >
        <WrappedComponent {...(props as P & JSX.IntrinsicAttributes)} />
      </SentryErrorBoundary>
    ) : (
      <SentryErrorBoundary boundary={options.boundary} page={options.page} isPageLevel={options.isPageLevel} fallback={options.fallback}>
        <WrappedComponent {...(props as P & JSX.IntrinsicAttributes)} />
      </SentryErrorBoundary>
    )

  const wrappedName = WrappedComponent.displayName || WrappedComponent.name || options.boundary || 'Component'
  ComponentWithBoundary.displayName = `withErrorBoundary(${wrappedName})`
  return ComponentWithBoundary
}

export default SentryErrorBoundary
